/**
 * Parse endpoint information from markdown content
 */

export interface EndpointInfo {
  method: string;
  path: string;
  title: string;
  pathParams?: Array<{ name: string; type: string; required: boolean; description: string }>;
  queryParams?: Array<{ name: string; type: string; required: boolean; description: string }>;
  hasBody?: boolean;
}

/**
 * Extract endpoint information from markdown content
 * Looks for patterns like:
 * - ## Endpoint Title
 * - ```\nGET /api/seasons\n```
 * - Path Parameters table
 * - Query Parameters table
 */
export function parseEndpointsFromMarkdown(content: string): EndpointInfo[] {
  const endpoints: EndpointInfo[] = [];
  
  // Split content by h2 headings (##)
  const sections = content.split(/^##\s+/m);
  
  for (const section of sections) {
    if (!section.trim()) continue;
    
    const lines = section.split('\n');
    const title = lines[0].trim();
    
    // Skip if title doesn't look like an endpoint title (too generic)
    if (title.toLowerCase().includes('endpoint') || title.toLowerCase().includes('authentication')) {
      continue;
    }
    
    // Look for endpoint code block (```\nMETHOD /api/path\n``` or ```METHOD /api/path```)
    // Match code blocks that contain HTTP method and /api/ path
    const endpointMatch = section.match(/```\s*\n?([A-Z]+)\s+(\/api\/[^\s\n`]+)/);
    
    if (endpointMatch) {
      const method = endpointMatch[1];
      const path = endpointMatch[2];
      
      // Extract path parameters from the path
      const pathParams: EndpointInfo['pathParams'] = [];
      const pathParamMatches = path.matchAll(/:(\w+)/g);
      for (const match of pathParamMatches) {
        pathParams.push({
          name: match[1],
          type: 'string',
          required: true,
          description: '',
        });
      }
      
      // Look for Path Parameters table
      const pathParamsTable = extractTable(section, 'Path Parameters');
      if (pathParamsTable && pathParams.length > 0) {
        pathParams.forEach((param, index) => {
          const row = pathParamsTable[index];
          if (row) {
            // Strip backticks from parameter name (markdown tables use backticks for code)
            param.name = (row[0] || param.name).replace(/`/g, '').trim();
            param.type = (row[1] || param.type).replace(/`/g, '').trim();
            param.required = row[2]?.toLowerCase() === 'yes' || false;
            param.description = row[3] || param.description;
          }
        });
      }
      
      // Look for Query Parameters table
      const queryParamsTable = extractTable(section, 'Query Parameters');
      const queryParams: EndpointInfo['queryParams'] = [];
      if (queryParamsTable) {
        queryParamsTable.forEach((row) => {
          queryParams.push({
            // Strip backticks from parameter name (markdown tables use backticks for code)
            name: (row[0] || '').replace(/`/g, '').trim(),
            type: (row[1] || 'string').replace(/`/g, '').trim(),
            required: row[2]?.toLowerCase() === 'yes' || false,
            description: row[3] || '',
          });
        });
      }
      
      // Check if there's a request body section (for POST/PUT/PATCH)
      const hasBody = /request body|body|request/i.test(section) && 
                     (method === 'POST' || method === 'PUT' || method === 'PATCH');
      
      endpoints.push({
        method,
        path,
        title,
        pathParams: pathParams.length > 0 ? pathParams : undefined,
        queryParams: queryParams.length > 0 ? queryParams : undefined,
        hasBody,
      });
    }
  }
  
  return endpoints;
}

/**
 * Extract table data from markdown section
 */
function extractTable(section: string, tableTitle: string): string[][] | null {
  // Look for the table after the title (e.g., "### Path Parameters")
  const titleRegex = new RegExp(`###\\s+${tableTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^]*?\\|`, 'i');
  const match = section.match(titleRegex);
  
  if (!match) return null;
  
  const tableStart = match.index! + match[0].length;
  const remaining = section.substring(tableStart);
  
  // Extract table rows (skip header separator row)
  const lines = remaining.split('\n');
  const rows: string[][] = [];
  let inTable = false;
  
  for (const line of lines) {
    if (line.trim().startsWith('|') && line.includes('---')) {
      inTable = true;
      continue;
    }
    
    if (inTable && line.trim().startsWith('|')) {
      const cells = line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell.length > 0);
      
      if (cells.length > 0) {
        rows.push(cells);
      }
    } else if (inTable && !line.trim().startsWith('|')) {
      // End of table
      break;
    }
  }
  
  return rows.length > 0 ? rows : null;
}

